import React, { useState, useMemo } from "react";
import TreeView, { flattenTree } from "react-accessible-treeview";
import cx from "classnames";
import sanitize from "helpers/sanitize";
import { useTranslation } from "translation/useTranslation";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";

const log_keys = {
  EMPLOYEE_NO_FIXED_AMMOUNT: "Beschäftigter erhält keinen Festbetrag",
  FLOOR_REDUCTION_FACTOR: "Floor-Reduktionsfaktor",
  FLOOR_AMOUNT_EMPLOYEE_BY_REDUCTION_FACTORS: "Berechneter Floor-Betrag anhand des Kürzungsfaktors",
  NORMALIZED_PERFORMANCE_POINTS_POINT_VALUE_MODEL: "Normalisierte Leistungspunkte",
  PERFORMANCE_RELATED_PAY: "Leistungsentgelt (in Euro)",
  SUM_PERFORMANCE_RELATED_PAY_POINT_VALUE: "Gesamtes Leistungsentgelt",
  CAPPING_NO_CAPPING: "Es wurde keine Deckelung vorgenommen",
  NORMALIZE_BUDGET: "Gekürztes Budget",
  FLOOR_CONSIDERED_EMPLOYEES_NOT_EXCLUDED: "Floor-Berechnung (nicht ausgeschlossene Beschäftigte)",
  FLOOR_AMOUNT: "Gesamtes Floorbudget (fester Wert)",
  FLOOR_BY_REDUCTION_FACTORS: "Floorverteilung auf Basis der Reduktionsfaktoren",
  FLOOR_NORMALIZE_BUDGET: "Kürzung des Budgets um die Floorbeträge",
  POINT_VALUE_VARIABLE: "Punktwert (in Euro)",
  DEFAULT_ERROR: "Es ist ein Fehler aufgetreten",
  OVERRIDE_BUDGET: "OVERRIDE_BUDGET",
  FICTITIOUS_BUDGET: "FICTITIOUS_BUDGET",
  EMPLOYEE_FIXED_AMMOUNT: "Festbetrag",
  NORMALIZE_BUDGET_SUM_FIXED_AMOUNT_GREATER_THAN_BUDGET: "Summe aller Festbeträge übersteigt das Budget",
  PAY_CRITERIA: "Entgeltfaktor",
  FLOOR_CONSIDERED_EMPLOYEES_NOT_EXCLUDED_WITHOUT_MINIMUM_PERFORMANCE: "FLOOR_CONSIDERED_EMPLOYEES_NOT_EXCLUDED_WITHOUT_MINIMUM_PERFORMANCE",
  FLOOR_CONSIDERED_EMPLOYEES_ALL: "FLOOR_CONSIDERED_EMPLOYEES_ALL",
  FLOOR_ONLY: "FLOOR_ONLY",
  FLOOR_PERCENT: "FLOOR_PERCENT",
  NO_FLOOR_BUDGET: "NO_FLOOR_BUDGET",
  NO_FLOOR_EMPLOYEES: "NO_FLOOR_EMPLOYEES",
  NO_FLOOR: "NO_FLOOR",
  SCHEMA_VALIDATION_FAILED: "Schemavalidierung fehlgeschlagen",
  NORMALIZED_PERFORMANCE_POINTS_OVER_100_GUARANTEE_MODEL_PERFORMANCE_OVER_HUNDRED: "Normalisierte Leistungspunkte über 100 % (Garantiewertmodel)",
  NORMALIZED_PERFORMANCE_POINTS_OVERALL_PERFORMANCE_GUARANTEE_MODEL_PERFORMANCE_RESULT: "Normalisierte Leistungspunkte (Gesamtgergebnis)",
  NORMALIZED_PERFORMANCE_POINTS_OVERALL_PERFORMANCE_GUARANTEE_MODEL_PER_CAPITA: "Normalisierte Leistungspunkte (nach Köpfen)",
  NORMALIZED_PERFORMANCE_POINTS_OVERALL_PERFORMANCE_GUARANTEE_MODEL_PERFORMANCE_AND_SALARY: "Normalisierte Leistungspunkte (Leistungsergebnis und Entgeltfaktor)",
  NORMALIZED_PERFORMANCE_POINTS_OVER_100_GUARANTEE_MODEL_NOT_CONSIDERED: "Normalisierte Leistungspunkte (über 100) unberücksichtigt",
  NORMALIZED_PERFORMANCE_POINTS_UP_TO_100_GUARANTEE_MODEL: "Normalisierte Leistungspunkte bis 100% (Garantiewertmodel)",
  NORMALIZED_PERFORMANCE_POINTS_GUARANTEE_MODEL_REDUCTION_FACTOR: "Normalisierte Leistungspunkte Garantiewertmodel Kürzungsfaktor",
  POINT_VALUE_GUARANTEE: "Punktwert (Garantie)",
  POINT_VALUE_FIXED: "Punktwert (fest)",
  PERFORMANCE_RELATED_PAY_GUARANTEE_OVER_100_PERCENT: "PERFORMANCE_RELATED_PAY_GUARANTEE_OVER_100_PERCENT",
  SUM_PERFORMANCE_RELATED_PAY_GUARANTEE_MODEL: "Gesamtes Leistungsentgelt (Garantiewertmodel)",
  CAPPING_FIXED_AMOUNT_GREATER_THAN_OR_EQUAL: "CAPPING_FIXED_AMOUNT_GREATER_THAN_OR_EQUAL",
  CAPPING_PERFORMANCE_RELATED_PAY_GREATER_THAN: "CAPPING_PERFORMANCE_RELATED_PAY_GREATER_THAN",
  CAPPING_REMAINIG_BUDGET_BY_PERFORMANCE_RESULT: "CAPPING_REMAINIG_BUDGET_BY_PERFORMANCE_RESULT",
  CAPPING_REMAINIG_BUDGET_BY_HEADS: "CAPPING_REMAINIG_BUDGET_BY_HEADS",
  CAPPING_REMAINIG_BUDGET_BY_PERFORMANCE_RESULT_PAY_FACTOR_REDUCTION_FACTOR: "CAPPING_REMAINIG_BUDGET_BY_PERFORMANCE_RESULT_PAY_FACTOR_REDUCTION_FACTOR",
  SUM_PAYOUT_EQUALS_BUDGET: "Summe aller Auszahlungen entspricht dem Budget. Keine weitere Berechnung möglich.",
  SUM_PAYOUT_GREATER_THAN_BUDGET_SWITCH_TO_POINT_VALUE_MODEL_VARIABLE: "Summe aller Auszahlungen übersteigt das Budget. Berechnungsmodel Punktwert variabel wird verwendet.",
  SUM_PAYOUT_GREATER_THAN_BUDGET: "Summe aller Auszahlungen übersteigt das Budget",
  SUM_PAYOUT_LOWER_THAN_BUDGET: "Summe aller Beträge unterschreitet das Budget",
  PRE_PROCESSING: "Vorberechnung / Festsetzung",
  FLOOR: "Floor",
  CALCULATION: "Berechnung",
  EMPLOYEE_LOGGING: "Abschlussberechnung",
  CAPPING: "Deckelung",
  EMPLOYEE_FIXED_AMOUNT: "Festbetrag",
  EMPOYEE_FIXED_AMOUNT: "Festbetrag",
  EMPLOYEE_NO_FIXED_AMOUNT: "Festbetrag",
  EMPOYEE_NO_FIXED_AMOUNT: "Festbetrag"
};


// Convert JSON to nested tree data
function buildTreeDataFromJson(json, employees, stepOnly, editableTranslate) {
  if (stepOnly) {
    let calculationSteps = json.scoreCard?.calculationSteps || json.employee?.calculationSteps || json.calculationSteps || [];
    return {
      name: "",
      children: groupLogsToTreeNodes(calculationSteps, editableTranslate),
      metadata: { default_expanded: true, with_arrow: true },
    };
  }

  return {
    name: "",
    children: json.employees.map((employee) => {
      const emp = employees.find((e) => e.uuid === employee.id);
      const employeeNode = {
        name: emp ? emp.name : `Employee: ${employee.id}`,
        children: [],
        metadata: { default_expanded: false, with_arrow: true },
      };

      // Scorecards
      employee.scoreCards.forEach((scoreCard, i) => {
        const scoreCardNode = {
          name: `LK ${i + 1}`,
          children: groupLogsToTreeNodes(scoreCard.calculationSteps, editableTranslate),
          metadata: { default_expanded: false, with_arrow: true },
        };
        employeeNode.children.push(scoreCardNode);
      });

      // Employee logging
      if (Array.isArray(employee.calculationSteps) && employee.calculationSteps.length > 0) {
        employeeNode.children.push({
          name: "Abschlussberechnung",
          children: groupLogsToTreeNodes(employee.calculationSteps, editableTranslate),
          metadata: { default_expanded: false, with_arrow: true },
        });
      }

      return employeeNode;
    }),
  };
}

function resolveLogKey(key) {
  if (!key) return "";

  let category = key.toUpperCase().replace(/-/g, "_");
  return log_keys[category] || key;
}

function getTransKey(type, key) {
  if (!key) return "";
  let prefix = 'calculation_steps.';
  if (type === 'subcategory') prefix += 'subcategory.';
  if (type === 'category') prefix += 'category.';
  if (type === 'detail') prefix += 'details.';

  return prefix + key.replace(/ /g, '_').replace(/-/g, '_').toLowerCase();
}

function groupLogsToTreeNodes(logs, editableTranslate) {
  const grouped = {};

  logs.forEach((log) => {
    let category = "uncategorized";
    let subCategory = log.key
      ? `<span title="Old structure. Required to be updated" class="ml-1">⚠️</span> ${log_keys[log.key] || log.key}`
      : "no-description-key";

    if (log.category) {
      // category = log.category.toUpperCase().replace(/-/g, "_");
      // category = log_keys[category] || log.category;
      category = log.category;
    }

    if (log["descriptionKey"] || log['description_key']) {
      // subCategory = log["descriptionKey"].toUpperCase().replace(/-/g, "_");
      // subCategory = log_keys[subCategory] || log["descriptionKey"];
      subCategory = log["descriptionKey"] || log['description_key'];
    }

    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subCategory]) grouped[category][subCategory] = [];

    grouped[category][subCategory].push(log);
  });

  return Object.entries(grouped).map(([category, subCategories]) => ({
    name: `<b>${editableTranslate(getTransKey('category', category), resolveLogKey(category))}:</b>`,
    metadata: { default_expanded: true, with_arrow: true, type: 'category', title: editableTranslate(getTransKey('category', category), resolveLogKey(category)) },
    children: Object.entries(subCategories).map(([descKey, logs]) => ({
      name: `<span class="text-danger"><b>${editableTranslate(getTransKey('subcategory', descKey), resolveLogKey(descKey))}:</b></span>`,
      metadata: {
        default_expanded: true,
        with_arrow: false,
        type: 'subcategory',
        title: editableTranslate(getTransKey('subcategory', descKey), resolveLogKey(descKey)),
        description: logs?.[0]?.description || resolveLogKey(descKey)
      },
      children: logs.map((log) => {
        if (!log.details) {
          if (descKey.includes('fixed-amount') || descKey.includes('Festbetrag')) {
            log.details = {
              result: 'No fixed amount'
            };
          } else {
            return { name: `No details available` };
          }

        }

        const sortOrder = ["formula", "calculation", "result"];
        const sortedEnhancedDetails = Object.entries(log.details).sort(([a], [b]) => {
          const idxA = sortOrder.includes(a) ? sortOrder.indexOf(a) + 1000 : 0;
          const idxB = sortOrder.includes(b) ? sortOrder.indexOf(b) + 1000 : 0;
          return idxA - idxB;
        });

        return sortedEnhancedDetails.map(([key, value]) => {
          // Render array values as separate lines
          if (Array.isArray(value)) {
            return {
              name: `
                <div style="display: flex; gap: 8px;">
                  <div style="width: 100px; font-weight: bold; text-transform: capitalize;">${editableTranslate(getTransKey('detail', sanitize(key)), sanitize(key))}:</div>
                  <div>
                    ${value.map((item) => `<div>${sanitize(item)}</div>`).join("")}
                  </div>
                </div>
              `,
            };
          }

          // Single string value
          return {
            name: `
              <div style="display: flex; gap: 8px;">
                <div style="width: 100px; font-weight: bold; text-transform: capitalize;">${editableTranslate(getTransKey('detail', sanitize(key)), sanitize(key))}:</div>
                <div>${editableTranslate(getTransKey('detail', sanitize(value)), sanitize(value))}</div>
              </div>
            `,
          };
        });
      }).flat(),
    })),
  }));
}

function CalculationResultTreeView({ jsonData, employees, stepOnly = false}) {
  const { editableTranslateString, editableTranslate } = useTranslation();
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const treeData = useMemo(() => buildTreeDataFromJson(jsonData, employees, stepOnly, editableTranslateString), [jsonData, employees, stepOnly, editableTranslateString]);
  const data = useMemo(() => flattenTree(treeData), [treeData]);
  const defaultExpandedIds = useMemo(() => {
    const ids = new Set();
    data.forEach((node) => {
      if (node.metadata && node.metadata.default_expanded) {
        ids.add(node.id);
      }
    });
    return Array.from(ids);
  }, [data]);

  return (
    <div>
      {/* <div>
        <Button variant="secondary" onClick={() => setExpandedIds([])} data-testid="collapse-all-button">Unexpand All</Button>
      </div> */}
      <div className="checkbox">
        <TreeView
          data={data}
          aria-label="Employee Score Tree"
          defaultExpandedIds={defaultExpandedIds}
          className="basic"
          nodeRenderer={({
            element,
            isBranch,
            isExpanded,
            isDisabled,
            getNodeProps,
            level,
            handleExpand,
          }) => (
            <div
              {...getNodeProps({
                onClick: (event) => {
                  if (element.metadata?.type === 'subcategory') {
                    const description = element.metadata.description || element.metadata.title || '';
                    setSelectedSubcategory({
                      title: element.metadata.title || sanitize(element.name),
                      description,
                    });
                    return; // do not collapse/expand subcategories
                  }
                  handleExpand(event);
                },
              })}
              style={{
                marginLeft: 23 * (level - 1),
                opacity: isDisabled ? 0.5 : 1,
              }}
            >
              {isBranch && element.metadata && element.metadata.with_arrow && (
                <FAArrowIcon isOpen={isExpanded} />
              )}
              <span className="name">
                <span dangerouslySetInnerHTML={{ __html: sanitize(element.name) }} />
              </span>
            </div>
          )}
        />
      </div>

      {selectedSubcategory && (
        <Modal
          open={true}
          title={<span dangerouslySetInnerHTML={{ __html: sanitize(selectedSubcategory.title) }} />}
          onClose={() => setSelectedSubcategory(null)}
          testIdPrefix="subcategory-"
          footer={
            <Button variant="primary" onClick={() => setSelectedSubcategory(null)} data-testid="subcategory-close-action">
              {editableTranslate('button.close', 'Schließen')}
            </Button>
          }
        >
          <p className="font-weight-bold">{editableTranslate('calculation_steps.subcategory.description', 'Erläuterung')}:</p>
          <p className="mb-0">{selectedSubcategory.description}</p>
        </Modal>
      )}
    </div>
  );
}

// ▶️ Font Awesome arrow icon
const FAArrowIcon = ({ isOpen }) => {
  const classes = cx("fa", {
    "fa-caret-down": isOpen,
    "fa-caret-right": !isOpen,
  });
  return <i className={classes} style={{ marginRight: 6 }} />;
};

export default CalculationResultTreeView;
